import React, { useState } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdEmail, MdPerson, MdMessage, MdSend, MdCheckCircle, MdCategory, MdSubdirectoryArrowRight } from 'react-icons/md';

const SUPPORT_CATEGORIES = {
  'Account & Login': [
    'Cannot log in', 'Password reset not working', 'Google sign-in issue',
    'Account locked or suspended', 'Delete my account', 'Other'
  ],
  'Polls & Voting': [
    'Poll not loading', 'Cannot submit my vote', 'Inappropriate poll content',
    'Custom poll not approved', 'Missing poll points', 'Other'
  ],
  'Subscriptions & Billing': [
    'Payment failed', 'Cancel my subscription', 'Refund request',
    'Subscription not activating', 'Billing amount incorrect', 'Other'
  ],
  'Representative Profiles': [
    'Profile setup help', 'Update my representative info', 'Candidacy assessment question',
    'Profile not showing publicly', 'Other'
  ],
  'Bug Report': [
    'Page not loading', 'Feature not working as expected', 'Display or layout issue',
    'Performance issue', 'Other'
  ],
  'Feature Request': [
    'New poll feature', 'Profile improvement', 'General suggestion', 'Other'
  ],
  'Other': [
    'General question', 'Partnership inquiry', 'Media inquiry', 'Other'
  ]
};

/**
 * Reusable contact form component.
 * @param {string} variant - "light" (default, for white card backgrounds) or "dark" (for dark-themed pages)
 * @param {string|null} userId - Optional logged-in user ID
 */
export default function ContactForm({ variant = 'light', userId = null }) {
  const isDark = variant === 'dark';
  const [formData, setFormData] = useState({ name: '', email: '', category: '', subcategory: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const sanitizeInput = (input, maxLength) => {
    return input
      .replace(/[<>'`;{}[\]\\]/g, '')
      .replace(/--/g, '').replace(/\/\*/g, '').replace(/\*\//g, '')
      .substring(0, maxLength);
  };

  const sanitizeForSubmit = (input, maxLength) => {
    return input
      .replace(/[<>'`;{}[\]\\]/g, '')
      .replace(/(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|CREATE|TRUNCATE|EXEC|EXECUTE)\b)/gi, '')
      .replace(/--/g, '').replace(/\/\*/g, '').replace(/\*\//g, '')
      .substring(0, maxLength);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const maxLength = name === 'message' ? 1000 : 200;
    if (name === 'category') {
      setFormData(prev => ({ ...prev, category: value, subcategory: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: name === 'subcategory' ? value : sanitizeInput(value, maxLength) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name.trim()) { setError('Please enter your name'); return; }
    if (!formData.email.trim()) { setError('Please enter your email address'); return; }
    if (!formData.email.includes('@')) { setError('Please enter a valid email address'); return; }
    if (!formData.category) { setError('Please select a category'); return; }
    if (!formData.subcategory) { setError('Please select a subcategory'); return; }
    if (!formData.message.trim()) { setError('Please enter a message'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/support/contact`, {
        name: sanitizeForSubmit(formData.name, 200),
        email: sanitizeForSubmit(formData.email, 200),
        category: formData.category,
        subcategory: formData.subcategory,
        message: sanitizeForSubmit(formData.message, 1000),
        user_id: userId || null
      });
      setSubmitted(true);
      setFormData({ name: '', email: '', category: '', subcategory: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const subcategories = formData.category ? SUPPORT_CATEGORIES[formData.category] || [] : [];

  // Tailwind classes per variant
  const inputCls = isDark
    ? 'w-full px-4 py-3 bg-gray-800 border border-gray-600 text-white placeholder-gray-500 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary font-rubik text-sm'
    : 'w-full px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-secondary focus:border-secondary font-rubik text-sm';
  const selectCls = isDark
    ? 'w-full px-4 py-3 bg-gray-800 border border-gray-600 text-white rounded-xl focus:ring-2 focus:ring-primary focus:border-primary font-rubik text-sm disabled:bg-gray-900 disabled:text-gray-600'
    : 'w-full px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-secondary focus:border-secondary font-rubik text-sm bg-white disabled:bg-gray-100 disabled:text-gray-400';
  const labelCls = isDark
    ? 'block text-sm font-medium text-gray-300 mb-2 font-rubik'
    : 'block text-sm font-medium text-gray-700 mb-2 font-rubik';
  const iconCls = isDark ? 'inline mr-1 text-gray-400' : 'inline mr-1 text-gray-500';

  if (submitted) {
    return (
      <div className="text-center py-8" data-testid="contact-success-message">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <MdCheckCircle className="text-4xl text-green-600" />
        </div>
        <h3 className={`text-xl font-semibold font-rubik mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>Message Sent!</h3>
        <p className={`font-rubik mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Thank you for reaching out. We&apos;ll review your message and get back to you soon.
        </p>
        <button onClick={() => setSubmitted(false)} className="btn-secondary px-6 py-2" data-testid="send-another-btn">
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="contact-form">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-rubik" data-testid="contact-form-error">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className={labelCls}><MdPerson className={iconCls} /> Your Name</label>
          <input type="text" name="name" value={formData.name} onChange={handleInputChange} maxLength={200}
            placeholder="Enter your name" className={inputCls} data-testid="contact-name-input" />
        </div>
        <div>
          <label className={labelCls}><MdEmail className={iconCls} /> Email Address</label>
          <input type="email" name="email" value={formData.email} onChange={handleInputChange} maxLength={200}
            placeholder="Enter your email address" className={inputCls} data-testid="contact-email-input" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className={labelCls}><MdCategory className={iconCls} /> Category</label>
          <select name="category" value={formData.category} onChange={handleInputChange} className={selectCls} data-testid="contact-category-select">
            <option value="">Select a category</option>
            {Object.keys(SUPPORT_CATEGORIES).map(cat => (<option key={cat} value={cat}>{cat}</option>))}
          </select>
        </div>
        <div>
          <label className={labelCls}><MdSubdirectoryArrowRight className={iconCls} /> Subcategory</label>
          <select name="subcategory" value={formData.subcategory} onChange={handleInputChange} disabled={!formData.category}
            className={selectCls} data-testid="contact-subcategory-select">
            <option value="">{formData.category ? 'Select a subcategory' : 'Select a category first'}</option>
            {subcategories.map(sub => (<option key={sub} value={sub}>{sub}</option>))}
          </select>
        </div>
      </div>

      <div className="mb-6">
        <label className={labelCls}><MdMessage className={iconCls} /> Message</label>
        <textarea name="message" value={formData.message} onChange={handleInputChange} maxLength={1000} rows={5}
          placeholder="Describe your issue or question in detail..."
          className={`${inputCls} resize-none`} data-testid="contact-message-input" />
        <p className={`text-xs mt-1 text-right font-rubik ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{formData.message.length}/1000</p>
      </div>

      <button type="submit" disabled={submitting}
        className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="contact-submit-btn">
        {submitting ? (
          <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Sending...</>
        ) : (
          <><MdSend className="text-lg" /> Send Message</>
        )}
      </button>
    </form>
  );
}
